import { useEffect, useState, useRef } from "preact/hooks";
import { Text, useText, MarkupText } from "preact-i18n";
import clsx from "clsx";
import { useMatomo } from "@datapunt/matomo-tracker-react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  IconButton,
  Popover,
  Typography,
} from "@mui/material";

import { Unsubscribe, Delete, Done, MoreVert } from "@mui/icons-material";

import { useAliasDemoQuery } from "../Settings/Settings";

import style from "./style.module.css";

const DemoActions = () => {
  return (
    <div class="h-full flex items-center">
      <div class="hidden md:block">
        <IconButton size="small">
          <Done />
        </IconButton>
      </div>

      <IconButton size="small">
        <Delete />
      </IconButton>

      <div class="hidden md:block">
        <IconButton size="small">
          <MoreVert />
        </IconButton>
      </div>
    </div>
  );
};

const DemoHeader = ({ expanded, subject, from, body }) => {
  return (
    <AccordionSummary>
      {expanded ? (
        <div class={clsx(style.wrapperExpanded, "w-full items-center")}>
          <h3 class="w-full">{subject}</h3>
          <div class="ml-2 w-full flex justify-end">
            <DemoActions />
          </div>
        </div>
      ) : (
        <div class={clsx(style.wrapper, "w-full items-center")}>
          <div class="ml-2 w-full inline-flex truncate">
            <Typography
              component="h2"
              noWrap
              className="w-full flex justify-start"
            >
              <span class="font-bold">{from}</span>
              &nbsp;
              <span dangerouslySetInnerHTML={{ __html: subject }} />
              &nbsp;
              <span
                class="italic text-ellipsis overflow-hidden"
                dangerouslySetInnerHTML={{
                  __html: body?.replace("<br />", "&nbsp;"),
                }}
              />
            </Typography>
          </div>
          <span class="ml-2 w-full flex justify-end">
            <DemoActions />
          </span>
        </div>
      )}
    </AccordionSummary>
  );
};

const NextButton = ({ children, locales, ...props }) => {
  return (
    <div class="flex justify-between mt-2">
      {children || <div />}
      <Button variant="outlined" {...props}>
        <Text id={locales} />
      </Button>
    </div>
  );
};

const FirstDemo = ({ onNext }) => {
  const { trackEvent } = useMatomo();
  const [anchor, setAnchor] = useState(null);

  const alias = useAliasDemoQuery("HUMAN");

  useEffect(() => {
    if (anchor) {
      anchor.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    }
  }, [anchor]);

  const { from, subject } = useText({
    from: "DashboardDemo.obfuscatedMessage.from",
    subject: "DashboardDemo.obfuscatedMessage.subject",
  });
  return (
    <>
      <Popover
        anchorEl={anchor}
        open={Boolean(anchor)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        onClose={() => setAnchor(null)}
      >
        <div class="p-4 w-96 max-w-full" onMouseLeave={() => setAnchor(null)}>
          <h3 class="font-bold text-md pb-2">
            <Text id="DashboardDemo.aliasHeading" />
          </h3>
          <p>
            <MarkupText id="DashboardDemo.whyAlias" fields={{ alias }} />
          </p>
          <NextButton
            onClick={() => {
              onNext();
              setAnchor(null);
              trackEvent({
                category: "main-landingpage",
                action: "click:DashboardDemo:Alias:nextButton",
              });
            }}
            locales="DashboardDemo.next"
          />
        </div>
      </Popover>

      <div
        class="relative flex items-center"
        onClick={(event) => {
          setAnchor(event.currentTarget);
          trackEvent({
            category: "main-landingpage",
            action: "click:DashboardDemo:Alias",
          });
        }}
      >
        <div class="w-2 h-2 rounded-full bg-black motion-safe:animate-ping cursor-pointer pointer-events-none absolute z-50 ml-12" />
        <Accordion expanded={false} className="w-full">
          <DemoHeader expanded={false} from={from} subject={subject} />
        </Accordion>
      </div>
    </>
  );
};

const SecondDemo = ({ onNext, expanded, setExpanded }) => {
  const { trackEvent } = useMatomo();
  const [animationDone, setAnimationDone] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (expanded) {
      window.setTimeout(() => {
        setAnimationDone(true);
        ref.current.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
      }, 300);
    }
  }, [ref, expanded, setAnimationDone]);

  const { from, subject, body } = useText({
    from: "DashboardDemo.unsubscribeMessage.from",
    subject: "DashboardDemo.unsubscribeMessage.subject",
    body: "DashboardDemo.unsubscribeMessage.body",
  });
  return (
    <>
      <Popover
        anchorEl={ref.current}
        open={Boolean(ref.current) && expanded && animationDone}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        onClose={() => {
          setExpanded(false);
          setAnimationDone(false);
        }}
      >
        <div class="p-4 w-96 max-w-full">
          <h3 class="font-bold text-md pb-2">
            <Text id="DashboardDemo.unsubscribe" />
          </h3>
          <p>
            <Text id="DashboardDemo.whyUnsubscribe" />
          </p>
          <NextButton
            onClick={() => {
              onNext();
              setExpanded(false);
              trackEvent({
                category: "main-landingpage",
                action: "click:DashboardDemo:unsubscribe:nextButton",
              });
            }}
            locales="DashboardDemo.next"
          />
        </div>
      </Popover>

      {!expanded && (
        <div class="w-2 h-2 rounded-full bg-black absolute motion-safe:animate-ping cursor-pointer pointer-events-none z-50 mr-64 mt-6 right-0" />
      )}
      <Accordion
        expanded={expanded}
        className="w-full"
        onClick={() => {
          setExpanded((previous) => !previous);
          trackEvent({
            category: "main-landingpage",
            action: "click:DashboardDemo:unsubscribe",
          });
        }}
        onAnimationEnd={() => setAnimationDone(true)}
      >
        <DemoHeader
          expanded={expanded}
          subject={subject}
          from={from}
          body={body}
        />

        <div class="w-full flex justify-center">
          <div class="w-11/12">
            <Divider />
          </div>
        </div>

        <AccordionDetails>
          <div class="w-full">
            <div class="flex w-full justify-between">
              <span>
                <span class="capitalize">
                  <Text id="DashboardDemo.from" />
                </span>
                &nbsp;
                <span class="font-bold">{from}</span>
              </span>
              <div class="flex justify-between items-center">
                <div class="italic hidden sm:block">
                  <Text
                    id="DashboardDemo.minutesAgo"
                    fields={{ minutes: 10 }}
                  />
                </div>
                <IconButton size="small" onClick={() => onComplete()} ref={ref}>
                  <Unsubscribe />
                </IconButton>
              </div>
            </div>
            <div dangerouslySetInnerHTML={{ __html: body }} />
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

const ThirdDemo = ({ onNext, expanded, setExpanded }) => {
  const { trackEvent } = useMatomo();
  const [animationDone, setAnimationDone] = useState(false);

  const ref = useRef();

  useEffect(() => {
    if (expanded) {
      window.setTimeout(() => {
        setAnimationDone(true);
        ref.current.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
      }, 300);
    }
  }, [ref, expanded, setAnimationDone]);

  const { from, subject, body } = useText({
    from: "DashboardDemo.block.from",
    subject: "DashboardDemo.block.subject",
    body: "DashboardDemo.block.body",
  });

  return (
    <>
      <Popover
        anchorEl={ref.current}
        open={Boolean(ref.current) && expanded && animationDone}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          pointerEvents: "none",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        disableRestoreFocus
      >
        <div class="p-4 w-96 max-w-full">
          <h3 class="font-bold text-md pb-2">
            <Text id="DashboardDemo.postalSecrecy" />
          </h3>
          <p>
            <Text id="DashboardDemo.blockPixels" />
          </p>

          <NextButton
            className="pointer-events-auto"
            onClick={() => {
              onNext();
              setExpanded(false);
              trackEvent({
                category: "main-landingpage",
                action: "click:DashboardDemo:antiTracking:nextButton",
              });
            }}
            locales="DashboardDemo.signMeUp"
          >
            <Button
              className="pointer-events-auto"
              onClick={() => {
                setExpanded(false);
                setAnimationDone(false);
              }}
            >
              <Text id="DashboardDemo.close" />
            </Button>
          </NextButton>
        </div>
      </Popover>

      {!expanded && (
        <div class="w-2 h-2 rounded-full bg-black absolute motion-safe:animate-ping cursor-pointer pointer-events-none z-50 ml-6 mt-6" />
      )}
      <Accordion
        expanded={expanded}
        className="w-full"
        onClick={() => {
          setExpanded(true);
          trackEvent({
            category: "main-landingpage",
            action: "click:DashboardDemo:antiTracking",
          });
        }}
        onAnimationEnd={() => setAnimationDone(true)}
        ref={ref}
      >
        <DemoHeader
          expanded={expanded}
          subject={subject}
          from={from}
          body={body}
        />

        <div class="w-full flex justify-center">
          <div class="w-11/12">
            <Divider />
          </div>
        </div>

        <AccordionDetails>
          <div class="w-full">
            <div class="flex w-full justify-between">
              <span>
                <span class="capitalize">
                  <Text id="DashboardDemo.from" />
                </span>
                &nbsp;
                <span class="font-bold">{from}</span>
              </span>
              <span class="italic">
                <Text id="DashboardDemo.minutesAgo" fields={{ minutes: 6 }} />
              </span>
            </div>
            <div dangerouslySetInnerHTML={{ __html: body }} />
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

const DashboardDemo = ({ onFinish }) => {
  const [secondExpanded, setSecondExpanded] = useState(false);
  const [thirdExpanded, setThirdExpanded] = useState(false);

  return (
    <div class="w-full h-full relative">
      <FirstDemo
        onNext={() => {
          setSecondExpanded(true);
        }}
      />
      <SecondDemo
        expanded={secondExpanded}
        setExpanded={setSecondExpanded}
        onNext={() => {
          setThirdExpanded(true);
        }}
      />
      <ThirdDemo
        expanded={thirdExpanded}
        setExpanded={setThirdExpanded}
        onNext={() => {
          window.setTimeout(() => {
            onFinish();
          }, 300);
        }}
      />
    </div>
  );
};

export default DashboardDemo;
